import { useState, useEffect, useCallback, useMemo } from 'react'
import { useToken, useLogout } from '../auth/hooks'

const initialState = {
  response: null,
  error: null,
  isLoading: true,
}

const useFetch = (url, options, deps = [], predicate = true) => {
  const [state, setState] = useState(initialState)
  const [refreshCount, setRefreshCount] = useState(0)
  const token = useToken()
  const logout = useLogout()

  if (token) {
    options = {
      ...options,
      headers: {
        Authorization: `Bearer ${token}`,
        ['Cache-Control']: 'no-cache',
        Pragma: 'no-cache',
        ['Content-Type']: 'application/json',
        ...(options && options.headers),
      },
    }
  }

  const refresh = useCallback(
    () => setRefreshCount((count) => count + 1), // will trigger useEffect to rerun
    []
  )

  const controller = useMemo(
    () => window.AbortController && new window.AbortController(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps, refreshCount] // recalculate a new controller if refresh initiated
  )
  const cancel = useCallback(() => controller && controller.abort(), [
    controller,
  ])

  useEffect(
    () => {
      const fetchData = async () => {
        setState({ ...state, isLoading: true })
        options.onStart && options.onStart()
        try {
          const res = await fetch(url, {
            ...options,
            signal: controller && controller.signal,
          })
          if (res.ok) {
            const json = await res.json()
            setState({
              response: json,
              isLoading: false,
              error: null,
            })
            options.onSuccess && options.onSuccess(json)
          } else {
            if (res.status === 401) {
              // If you hit an api endpoint you're not allowed to, you're logged out.
              logout()
            } else {
              setState({
                response: null,
                error: res.statusText,
                isLoading: false,
              })
              options.onError && options.onError(res.statusText)
            }
          }
        } catch (error) {
          setState({
            response: null,
            error,
            isLoading: false,
          })
          options.onError && options.onError(error)
        }
      }
      if (predicate) fetchData()
      else setState({ ...state, isLoading: false })
      return cancel
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [...deps, predicate, refreshCount] // only rerun when refreshcount is incremented
  )
  return [
    state,
    {
      refresh,
      cancel,
    },
  ]
}

export default useFetch
